import { useCallback, useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import {
  DEFAULT_SMART_HOME_UI,
  getAdminSmartHomeUi,
  saveAdminSmartHomeUi,
  type SmartHomeEcosystemCopy,
  type SmartHomeUiCopy,
} from '../../api';
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure';
import { useHaptic } from '../../hooks/useHaptic';

function linesToList(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function listToLines(items: string[] | undefined): string {
  return (items || []).join('\n');
}

function mergeCopy(data: SmartHomeUiCopy): SmartHomeUiCopy {
  const base = DEFAULT_SMART_HOME_UI;
  return {
    page: { ...base.page, ...data.page },
    ecosystems: base.ecosystems.map((eco) => ({
      ...eco,
      ...(data.ecosystems.find((e) => e.id === eco.id) || {}),
    })),
    protocols: base.protocols.map((p) => ({
      ...p,
      ...(data.protocols.find((x) => x.id === p.id) || {}),
    })),
    chooser: base.chooser.map((row) => ({
      ...row,
      ...(data.chooser.find((x) => x.id === row.id) || {}),
    })),
  };
}

export function SmartHomeUiAdminPanel() {
  const haptic = useHaptic();
  const [copy, setCopy] = useState<SmartHomeUiCopy>(DEFAULT_SMART_HOME_UI);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCopy(mergeCopy(await getAdminSmartHomeUi()));
    } catch {
      setCopy(DEFAULT_SMART_HOME_UI);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateEco = (id: string, patch: Partial<SmartHomeEcosystemCopy>) => {
    setCopy((prev) => ({
      ...prev,
      ecosystems: prev.ecosystems.map((e) => (e.id === id ? { ...e, ...patch } : e)),
    }));
  };

  const handleSave = async () => {
    haptic.selection();
    setSaving(true);
    setMessage(null);
    try {
      setCopy(mergeCopy(await saveAdminSmartHomeUi(copy)));
      haptic.success();
      setMessage('Вкладка «Умный дом» сохранена.');
    } catch {
      haptic.error();
      setMessage('Не удалось сохранить.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-xs py-4 text-center" style={{ color: 'var(--text-3)' }}>Загрузка…</p>;
  }

  const pageFields: Array<{ key: keyof SmartHomeUiCopy['page']; label: string }> = [
    { key: 'title', label: 'Заголовок страницы' },
    { key: 'subtitle', label: 'Подзаголовок под заголовком' },
    { key: 'ecosystems_section_title', label: 'Заголовок блока «Экосистемы»' },
    { key: 'protocols_section_title', label: 'Заголовок блока «Протоколы»' },
    { key: 'chooser_section_title', label: 'Заголовок «Как выбрать протокол?»' },
  ];

  return (
    <div className="space-y-4">
      <p className="text-xs leading-relaxed" style={{ color: 'var(--text-3)' }}>
        Тексты нижней вкладки <strong style={{ color: 'var(--text-2)' }}>Умный дом</strong> в Mini App: карточки Xiaomi, Tuya, Matter,
        протоколы и подсказки «как выбрать». Список шагов и требований — по одной строке на пункт.
      </p>

      <AdminGlassDisclosure title="Шапка страницы" accent="cyan" defaultOpen>
        <div className="space-y-3">
          {pageFields.map(({ key, label }) => (
            <label key={key} className="block">
              <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>{label}</span>
              <input
                value={copy.page[key]}
                onChange={(e) => setCopy((prev) => ({ ...prev, page: { ...prev.page, [key]: e.target.value } }))}
                className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
              />
            </label>
          ))}
        </div>
      </AdminGlassDisclosure>

      <AdminGlassDisclosure title="Экосистемы (плитки и модалки)" accent="violet">
        <div className="space-y-4">
          {copy.ecosystems.map((eco) => (
            <div
              key={eco.id}
              className="rounded-xl p-3 space-y-2"
              style={{ border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.03)' }}
            >
              <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--cyan)' }}>
                {eco.name} · {eco.id}
              </p>
              <div className="flex gap-2">
                <label className="block shrink-0 w-14">
                  <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Иконка</span>
                  <input
                    value={eco.icon}
                    onChange={(e) => updateEco(eco.id, { icon: e.target.value })}
                    className="input-glass w-full rounded-xl px-2 py-2 text-sm mt-0.5 text-center"
                  />
                </label>
                <label className="block flex-1">
                  <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Название</span>
                  <input
                    value={eco.name}
                    onChange={(e) => updateEco(eco.id, { name: e.target.value })}
                    className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Подпись на плитке</span>
                <input
                  value={eco.tagline}
                  onChange={(e) => updateEco(eco.id, { tagline: e.target.value })}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                />
              </label>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Протоколы (через запятую)</span>
                <input
                  value={eco.protocols.join(', ')}
                  onChange={(e) =>
                    updateEco(eco.id, {
                      protocols: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    })
                  }
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                />
              </label>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Описание в модалке</span>
                <textarea
                  value={eco.description}
                  rows={3}
                  onChange={(e) => updateEco(eco.id, { description: e.target.value })}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5 resize-none"
                />
              </label>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Как подключить (строка = шаг)</span>
                <textarea
                  value={listToLines(eco.how_to)}
                  rows={4}
                  onChange={(e) => updateEco(eco.id, { how_to: linesToList(e.target.value) })}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5 resize-none"
                />
              </label>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Что нужно (строка = пункт)</span>
                <textarea
                  value={listToLines(eco.requirements)}
                  rows={2}
                  onChange={(e) => updateEco(eco.id, { requirements: linesToList(e.target.value) })}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5 resize-none"
                />
              </label>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Ограничения (строка = пункт)</span>
                <textarea
                  value={listToLines(eco.limitations)}
                  rows={2}
                  onChange={(e) => updateEco(eco.id, { limitations: linesToList(e.target.value) })}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5 resize-none"
                />
              </label>
            </div>
          ))}
        </div>
      </AdminGlassDisclosure>

      <AdminGlassDisclosure title="Протоколы (список карточек)">
        <div className="space-y-3">
          {copy.protocols.map((p, index) => (
            <div
              key={p.id}
              className="rounded-xl p-3 space-y-2"
              style={{ border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.03)' }}
            >
              <p className="text-xs font-semibold" style={{ color: 'var(--text-1)' }}>{p.name}</p>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Название</span>
                <input
                  value={p.name}
                  onChange={(e) => {
                    const protocols = [...copy.protocols];
                    protocols[index] = { ...p, name: e.target.value };
                    setCopy((prev) => ({ ...prev, protocols }));
                  }}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                />
              </label>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Описание</span>
                <textarea
                  value={p.desc}
                  rows={2}
                  onChange={(e) => {
                    const protocols = [...copy.protocols];
                    protocols[index] = { ...p, desc: e.target.value };
                    setCopy((prev) => ({ ...prev, protocols }));
                  }}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5 resize-none"
                />
              </label>
            </div>
          ))}
        </div>
      </AdminGlassDisclosure>

      <AdminGlassDisclosure title="Как выбрать протокол?">
        <div className="space-y-3">
          {copy.chooser.map((row, index) => (
            <div
              key={row.id}
              className="rounded-xl p-3 space-y-2"
              style={{ border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.03)' }}
            >
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Вопрос / ситуация</span>
                <input
                  value={row.q}
                  onChange={(e) => {
                    const chooser = [...copy.chooser];
                    chooser[index] = { ...row, q: e.target.value };
                    setCopy((prev) => ({ ...prev, chooser }));
                  }}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                />
              </label>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Рекомендация</span>
                <input
                  value={row.a}
                  onChange={(e) => {
                    const chooser = [...copy.chooser];
                    chooser[index] = { ...row, a: e.target.value };
                    setCopy((prev) => ({ ...prev, chooser }));
                  }}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                />
              </label>
            </div>
          ))}
        </div>
      </AdminGlassDisclosure>

      {message && (
        <p className="text-xs px-1" style={{ color: message.includes('сохран') ? '#9AE6B4' : '#FC814A' }}>
          {message}
        </p>
      )}
      <button
        type="button"
        disabled={saving}
        onClick={() => void handleSave()}
        className="btn-glow w-full text-white py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2"
      >
        <Save size={16} />
        {saving ? 'Сохранение…' : 'Сохранить вкладку «Умный дом»'}
      </button>
    </div>
  );
}
